/**
 * CompositionGuideToggle ✦ iter199 — operator-only long-press in
 * the bottom-LEFT corner of the launcher.
 *
 * Mirror of the RetuneRoomButton mechanic (hidden chrome, 900ms
 * hold, hairline ring fill, no visible affordance until the press
 * starts) but persists a sessionStorage flag instead of clearing
 * pairing. Drives the FramingGuide overlay across every booth
 * capture surface.
 *
 * Constitutional locks (per iter199):
 *   • Only rendered on the launcher + QR continuity views — never
 *     during a guest capture flow (guests must never discover this).
 *   • No visible chrome until the press is in progress.
 *   • Editorial italic copy revealed only as the ring fills:
 *       "Composition guide on ✦" / "Composition guide off ✦"
 *   • Persisted in sessionStorage so the toggle survives the
 *     guide's auto-dissolve schedule.
 *
 * The visible mechanism for hosts to enable framing calibration —
 * a 0.9s hold once before the night begins is enough to keep the
 * guide visible on the first 6s of every camera surface they walk
 * through during set-up.
 */
import React, { useCallback, useRef, useState } from 'react';
import { SERIF } from './boothShellAtoms';
import {
  setCompositionGuidePersisted,
  useCompositionGuideToggle,
} from './FramingGuide';

const HOLD_MS = 900;

const CompositionGuideToggle = ({ theme }) => {
  const { on } = useCompositionGuideToggle();
  const [progress, setProgress] = useState(0);
  const [flashCopy, setFlashCopy] = useState('');
  const timerRef = useRef(null);
  const startRef = useRef(0);

  const tick = useCallback(() => {
    const elapsed = Date.now() - startRef.current;
    const p = Math.min(1, elapsed / HOLD_MS);
    setProgress(p);
    if (p < 1) {
      timerRef.current = requestAnimationFrame(tick);
    } else {
      const next = !on;
      setCompositionGuidePersisted(next);
      setFlashCopy(next ? 'Composition guide on ✦' : 'Composition guide off ✦');
      setTimeout(() => setFlashCopy(''), 1400);
      setProgress(0);
    }
  }, [on]);

  const begin = () => {
    startRef.current = Date.now();
    setProgress(0);
    timerRef.current = requestAnimationFrame(tick);
  };
  const cancel = () => {
    if (timerRef.current) cancelAnimationFrame(timerRef.current);
    timerRef.current = null;
    setProgress(0);
  };

  return (
    <button
      type="button"
      onPointerDown={begin}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      data-testid="booth-composition-toggle"
      aria-label="Hold to toggle the composition guide"
      style={{
        position: 'fixed', left: 18, bottom: 18,
        width: 56, height: 56,
        background: 'transparent', border: 'none',
        cursor: 'pointer', padding: 0,
        zIndex: 5,
        outline: 'none', appearance: 'none',
      }}
    >
      {(progress > 0 || flashCopy) && (
        <>
          {progress > 0 && (
            <span style={{
              position: 'absolute', inset: 4,
              borderRadius: '50%',
              background: `conic-gradient(${theme.gold} ${progress * 360}deg, transparent 0deg)`,
              WebkitMask: 'radial-gradient(circle, transparent 56%, #000 58%)',
                      mask: 'radial-gradient(circle, transparent 56%, #000 58%)',
              opacity: 0.85,
            }} />
          )}
          {(progress > 0 || flashCopy) && (
            <span style={{
              position: 'absolute',
              left: 64, bottom: 18,
              whiteSpace: 'nowrap',
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 12, color: theme.gold,
              letterSpacing: '0.08em',
              opacity: flashCopy ? 1 : Math.min(1, progress * 1.4),
              pointerEvents: 'none',
              transition: 'opacity 320ms ease',
            }}>
              {flashCopy || (on ? 'Composition guide off ✦' : 'Composition guide on ✦')}
            </span>
          )}
        </>
      )}
    </button>
  );
};

export default CompositionGuideToggle;
